import { useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import {
    listAppWorkerGroups,
    loadMoreAppWorkerGroups
} from "../Actions/API/WorkerGroupActions"
import { APIAppWorkerGroup } from "../Models/API/AppWorkerGroup"
import { FetchState } from "../Models/Client/FetchState"
import { getItemList, getPageToFetch } from "../Models/Client/RemoteData"
import { SortExpression } from "../Models/Client/SortExpression"
import { Result } from "../Models/Utility/Result"
import { MainThunkDispatch } from "../Reducers"
import { useAppState } from "./useReduxState"

export function useAppWorkerGroupList(
    appID: string,
    sort: SortExpression<APIAppWorkerGroup>
): [
    FetchState<{
        items: APIAppWorkerGroup[]
        total: number
    }>,
    () => Promise<void>
] {
    const entry = useAppState(state => state.api.workerGroups.app[appID])
    const dispatch = useDispatch<MainThunkDispatch>()
    const handleLoadMore = useCallback(
        () => dispatch(loadMoreAppWorkerGroups(appID)),
        [appID, dispatch]
    )

    useEffect(() => {
        const page = entry === undefined ? 0 : getPageToFetch(entry, sort)

        if (
            page >= 0 ||
            JSON.stringify(sort) !== JSON.stringify(entry?.list?.params)
        ) {
            dispatch(listAppWorkerGroups(appID, page, sort))
        }
    }, [dispatch, entry, sort, appID])

    return [
        useMemo(() => {
            if (entry === undefined || entry.list === undefined) {
                return undefined
            }

            return Result.ok({
                items: getItemList(entry),
                total: entry.list.total
            })
        }, [entry]),
        handleLoadMore
    ]
}
